/**
 * CueSesh Application Entry Point
 *
 * Landing page for mobile app launch + Admin dashboard routing.
 *
 * @module App
 */

import { useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Landing from './screens/Landing';
import Privacy from './screens/Privacy';
import Terms from './screens/Terms';
import Contact from './screens/Contact';
import EpisodeRedirect from './screens/EpisodeRedirect';
import ScreenErrorBoundary from './components/ScreenErrorBoundary';

// Lazy-load the admin-v2 operator console (not needed by public visitors)
const AdminV2Routes = lazy(() => import('./admin-v2'));

/**
 * 404 Not Found page
 */
const NotFound = () => (
  <div className="w-full h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
    <div className="text-center p-8 max-w-md">
      <h1 className="text-6xl font-bold text-white mb-4">404</h1>
      <p className="text-gray-400 mb-6">The page you're looking for doesn't exist.</p>
      <a href="/" className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-block">
        Go Home
      </a>
    </div>
  </div>
);

/**
 * Meta Pixel SPA page views on route changes.
 * GA4 page views are NOT sent from here: gtag sends the initial one (config in
 * public/index.html) and the stream's Enhanced Measurement sends one per
 * history change. The Pixel has no such auto-fire, so it is handled here.
 */
const AnalyticsTracker = () => {
  const location = useLocation();
  const firstRoute = useRef(true);

  useEffect(() => {
    // The index.html bootstrap already fires the INITIAL Pixel PageView, so only
    // subsequent route changes fire here (firing on first mount too doubled the
    // consented landing PageView count).
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  return null;
};

/**
 * Main application component
 * @returns {JSX.Element} The CueSesh application
 */
const App = () => {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<ScreenErrorBoundary screenName="Landing"><Landing /></ScreenErrorBoundary>} />
          <Route path="/privacy" element={<ScreenErrorBoundary screenName="Privacy"><Privacy /></ScreenErrorBoundary>} />
          <Route path="/terms" element={<ScreenErrorBoundary screenName="Terms"><Terms /></ScreenErrorBoundary>} />
          <Route path="/contact" element={<ScreenErrorBoundary screenName="Contact"><Contact /></ScreenErrorBoundary>} />
          <Route path="/investors" element={<Navigate to="/contact" replace />} />
          <Route path="/media" element={<Navigate to="/contact" replace />} />

          {/* Episode deep link redirect */}
          <Route path="/e/:code" element={<ScreenErrorBoundary screenName="Episode Redirect"><EpisodeRedirect /></ScreenErrorBoundary>} />

          {/* Admin dashboard v2 — operator surface (cron, ingest, llm, classifications, users, sessions) */}
          <Route
            path="/admin/v2/*"
            element={
              <Suspense fallback={<div className="w-full h-screen flex items-center justify-center bg-gray-900"><p className="text-gray-400">Loading...</p></div>}>
                <AdminV2Routes />
              </Suspense>
            }
          />

          {/* Bare /admin lands on the operator console */}
          <Route path="/admin" element={<Navigate to="/admin/v2" replace />} />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
